import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import RecommendationTip from '../components/RecommendationTip';
import { useEntityConfig } from '../utils/EntityConfigContext';
import '../styles/SkillsDevelopmentScorecard.scss';
import { themeVars } from '../config/theme';
import { useSectorElementKey } from '../utils/useSectorElementKey';

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);

const rowClass = (actual, target) => {
  if (actual === null || actual === 0) return 'sc-row--empty';
  return actual >= target ? 'sc-row--achieved' : 'sc-row--partial';
};

// Per-element display metadata. Adding a new sector's skills development variant means adding one entry here, not a new page file.

const SCORECARD_META = {
  skills_development: {
    title: 'Skills Development Scorecard',
    subtitle: 'B-BBEE Skills Development Element Calculation',
    backPath: '/skills-development/',
    docPath: '/skills-development/submit-datasheet',
    docSheetName: 'Skills Development Data Sheet',
    summaryLabel: 'Total Skills Development Points',
    achievementLabel: 'Skills Development Achievement',
  },
  skills_development_transport: {
    title: 'Skills Development Scorecard',
    subtitle: 'B-BBEE Skills Development Transport Element Calculation',
    backPath: '/skills-development/',
    docPath: '/skills-development/submit-datasheet/skills-development-transport',
    docSheetName: 'Road Freight Skills Development Data Sheet',
    summaryLabel: 'Total Skills Development Points',
    achievementLabel: 'Skills Development Achievement',
  },
  skills_development_specialised: {
    title: 'Skills Development Scorecard',
    subtitle: 'B-BBEE Skills Development Specialised Scorecard Element Calculation',
    backPath: '/skills-development/',
    docPath: '/skills-development/submit-datasheet/skills-development-specialised',
    docSheetName: 'Skills Development Data Sheet',
    summaryLabel: 'Total Skills Development Points',
    achievementLabel: 'Skills Development Achievement',
  },
};

// The sector variants of this element; useSectorElementKey picks the one this company has.
const SD_VARIANTS = ['skills_development', 'skills_development_transport', 'skills_development_specialised'];

const SkillsDevelopmentScorecard = () => {
  const navigate = useNavigate();
  const { elementKey: rawElementKey } = useParams();
  const requestedKey = (rawElementKey || 'skills_development').replace(/-/g, '_');
  const elementKey = useSectorElementKey(requestedKey, SD_VARIANTS); // null until resolved
  const meta = SCORECARD_META[elementKey || requestedKey] || SCORECARD_META.skills_development;

  const { entity_size } = useEntityConfig();
  const [results, setResults] = useState(null);
  const [spendSummary, setSpendSummary] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [filename, setFilename] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [noDataSheet, setNoDataSheet] = useState(false);
  const [noEap, setNoEap] = useState(false);
  const [CombinedPoints, setCombinedPoints] = useState(null);
  const [maxCombinedPoints, setMaxCombinedPoints] = useState(null);
  const [indicators, setIndicators] = useState([]);

  
  const applyData = (data) => {
    setResults(data.results);
    setSpendSummary(data.spend_summary || null);
    setTotalPoints(data.total_points);
    setFilename(data.filename);
    setIndicators(data.indicators || []);  //To pull indicators from backend instead of it being hardcoded in the frontend
    setNoDataSheet(false);
    setNoEap(false);
    setError(null);
    setCombinedPoints(null);

  };

  const fetchCombinedTotal = () => {
    api.get('/core/scorecard/combined-total/')
      .then((res) => {
        setCombinedPoints(res.data.combined_points);
        if (res.data.max_points != null) setMaxCombinedPoints(res.data.max_points);
      })
      .catch(() => {});
  };

  const calculateFromDataSheet = async () => {
    setCalculating(true);
    setError(null);
    setNoDataSheet(false);
    setNoEap(false);
    try {
      const res = await api.post('/core/scorecard/calculate_from_data_sheet/', { element: elementKey || requestedKey });
      applyData(res.data);
      fetchCombinedTotal();
    } catch (err) {
      if (err.response?.status === 404) setNoDataSheet(true);
      else if (err.response?.status === 422 && err.response?.data?.error?.includes('No EAP')) setNoEap(true);
      else setError(err.response?.data?.error || 'Failed to calculate from data sheet. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    if (!elementKey) return;
    setLoading(true);
    api.get(`/core/scorecard/get_scorecard/?element=${elementKey}`)
      .then((res) => { applyData(res.data); setLoading(false); })
      .catch((err) => {
        setLoading(false);
        if (err.response?.data?.indicators?.length) setIndicators(err.response.data.indicators);
        if (err.response?.status === 404) calculateFromDataSheet();
      });
  }, [elementKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCombinedTotal(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const resultMap = results
    ? Object.fromEntries(results.map((r) => [r.id, r]))
    : {};

  const maxPoints = indicators.reduce((s, ind) => s + (ind.points || 0), 0);

  let lastSection = null;

  return (
    <div className="sc-container themed" style={themeVars(elementKey || requestedKey)}>
      <div className="sc-header">
        <button className="sc-back" onClick={() => navigate(meta.backPath)}>← Back</button>
        <div>
          <h1>{meta.title}</h1>
          <p>{meta.subtitle}</p>
        </div>
      </div>

      <div className="sc-upload-bar">
        <div className="sc-upload-info">
          {loading
            ? <span className="sc-upload-hint">Loading…</span>
            : filename
              ? <span className="sc-upload-name">✓ Calculated from: <strong>{filename}</strong></span>
              : <span className="sc-upload-hint">No data sheet found</span>
          }
        </div>
        <div className="sc-upload-actions">
          <button
            className="sc-template-btn"
            onClick={() => navigate(meta.docPath)}
          >
            Go to Documentation
          </button>
          <button
            className="sc-upload-btn"
            onClick={calculateFromDataSheet}
            disabled={calculating || loading}
          >
            {calculating ? 'Calculating…' : 'Recalculate'}
          </button>
        </div>
      </div>

      {noDataSheet && (
        <div className="sc-error">
          No Skills Development Data Sheet uploaded yet. Go to{' '}
          <button className="sc-error-link" onClick={() => navigate(meta.docPath)}>
            Documentation
          </button>{' '}
          and upload the <strong>Skills Development Data Sheet</strong> Excel file.
        </div>
      )}

      {noEap && (
        <div className="sc-error">
          No EAP found for the selected province. Please update your province in{' '}
          <button className="sc-error-link" onClick={() => navigate('/finance-statutory/company-information')}>
            Company Information
          </button>{' '}
          or contact your administrator to load the EAP targets.
        </div>
      )}

      {error && <div className="sc-error">{error}</div>}


      {spendSummary && spendSummary.length > 0 && (
        <div className="sc-table-outer">
          <h2 className="sc-spend-title">SPEND BREAKDOWN BY PROGRAMME</h2>
          <div className="sc-table-wrapper">
            <table className="sc-table sc-spend-table">
              <thead>
                <tr>
                  <th>Programme</th>
                  <th>African Male</th>
                  <th>African Female</th>
                  <th>Coloured Male</th>
                  <th>Coloured Female</th>
                  <th>Indian Male</th>
                  <th>Indian Female</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {spendSummary.map((row) => {
                  const demos = ['african_male', 'african_female', 'coloured_male', 'coloured_female', 'indian_male', 'indian_female'];
                  const rowTotal = row.total != null
                    ? row.total
                    : demos.reduce((s, d) => s + (row[d] || 0), 0);
                  const targetTotal = demos.reduce((s, d) => s + (row.targets?.[d] || 0), 0);
                  return (
                    <React.Fragment key={row.program}>
                      <tr className="sc-row--program-header">
                        <td colSpan="8">{row.program}</td>
                      </tr>
                      <tr>
                        <td>Actual</td>
                        {demos.map((d) => (
                          <td key={d}>{row[d] != null ? row[d].toLocaleString() : '—'}</td>
                        ))}
                        <td><strong>{rowTotal.toLocaleString()}</strong></td>
                      </tr>
                      {row.program !== 'Absorption' && (
                        <tr className="sc-row--target">
                          <td>Target</td>
                          {demos.map((d) => (
                            <td key={d}>
                              {row.targets?.[d] != null ? Math.round(row.targets[d]).toLocaleString() : '—'}
                            </td>
                          ))}
                          <td><strong>{row.targets?.total != null ? Math.round(row.targets.total).toLocaleString() : Math.round(targetTotal).toLocaleString()}</strong></td>
                        </tr>
                      )}
                      {row.map_vals && Object.keys(row.map_vals).length > 0 && (
                        <tr className="sc-row--map">
                          <td>Max Points Available</td>
                          {demos.map((d) => (
                            <td key={d}>
                              {row.map_vals?.[d] != null ? row.map_vals[d].toFixed(2) : '—'}
                            </td>
                          ))}
                          <td><strong>{row.map_vals?.total != null ? row.map_vals.total.toFixed(2) : demos.reduce((s, d) => s + (row.map_vals?.[d] || 0), 0).toFixed(2)}</strong></td>
                        </tr>
                      )}
                      {row.spend_diff && Object.keys(row.spend_diff).length > 0 && (
                        <tr className="sc-row--shortfall">
                          <td>Shortfall Spend</td>
                          {demos.map((d) => (
                            <td key={d}>
                              {row.spend_diff?.[d] != null ? Math.round(row.spend_diff[d]).toLocaleString() : '—'}
                            </td>
                          ))}
                          <td><strong>{row.spend_diff?.total != null ? Math.round(row.spend_diff.total).toLocaleString() : Math.round(demos.reduce((s, d) => s + (row.spend_diff?.[d] || 0), 0)).toLocaleString()}</strong></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="sc-table-outer">
        <div className="sc-table-wrapper">
          <table className="sc-table">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Compliance Target</th>
                <th>Max Points</th>
                <th>Actual (%)</th>
                <th>Points Achieved</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((ind) => {
                const showHeader = ind.section && ind.section !== lastSection;
                lastSection = ind.section;

                const r = resultMap[ind.id];
                const actual = r?.actual ?? null;
                const pts = r?.points ?? 0;
                const isMax = pts >= ind.points;

                return (
                  <React.Fragment key={ind.id}>
                    {showHeader && (
                      <tr className="sc-row--section-header">
                        <td colSpan="5">{ind.section}</td>
                      </tr>
                    )}
                    <tr className={rowClass(actual, ind.target)}>
                      <td>{ind.label}</td>
                      <td>{ind.targetLabel ?? `${ind.target}%`}</td>
                      <td>{ind.points}</td>
                      <td>{actual !== null ? `${fmt(actual)}%` : '-'}</td>
                      <td>
                        <span className={`sc-points${isMax ? ' sc-points--max': pts === 0 ? ' sc-points--zero' : ''}`}>
                          {fmt(pts)}
                        </span>
                        <RecommendationTip
                        ind={ind}
                        actual={actual}
                        pts={pts}
                        spendRow={spendSummary?.find(r => r.program === ind.id)}
                        />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              <tr className={`sc-total-row${results?.some(r => r.id === '_total_sub_min' && r.sub_minimum_met === false) ? ' sc-row--submin-fail' : ''}`}>
                <td colSpan="3">Total</td>
                <td />
                <td>{fmt(totalPoints)} / {maxPoints || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {results?.some(r => r.sub_minimum_met === false) && (
          <div className="sc-submin-icon-wrap">
            <span className="sc-submin-icon-btn">⚠</span>
            <div className="sc-submin-popup">
              Sub-minimum not met — overall scorecard level will be discounted by one level.
            </div>
          </div>
        )}
      </div>

      <div className="sc-summary">
        <div className="sc-summary-card sc-summary-card--total">
          <div className="sc-summary-score">{fmt(totalPoints)} <span>/ {maxPoints || '—'}</span></div>
          <div className="sc-summary-label">Total Skills Development Points</div>
        </div>
        <div className="sc-summary-card">
          <div className="sc-summary-score">{CombinedPoints !== null ? fmt(CombinedPoints) : '-'} <span>/ {maxCombinedPoints ?? '—'}</span></div>
          <div className="sc-summary-label">Points (incl. bonus)</div>
        </div>
        <div className="sc-summary-card">
          <div className="sc-summary-score">{maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : '—'}<span>%</span></div>
          <div className="sc-summary-label">Skills Development Achievement</div>
        </div>
      </div>

    </div>
  );
};

export default SkillsDevelopmentScorecard;
